// =============================================================================
// Workers / Crew — manage the field crew used for Job Invoice worker-hours
// (legacy IMS Workers table behind Invoice_WorkerHours). List, add, edit pay
// rate, mark foreman, activate/deactivate.
// =============================================================================
import { useCallback, useEffect, useState } from 'react'
import { Plus, X, Save } from 'lucide-react'
import { listWorkers, saveWorker } from '../services/data'
import { PageHeader, Card, DataTable, Spinner, Button } from '../components/shared/ui'
import { currency } from '../lib/format'
import { toast } from 'react-toastify'
import type { Worker } from '../types'

export default function Workers() {
  const [workers, setWorkers] = useState<Worker[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Worker | 'new' | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try { setWorkers(await listWorkers(false)) }
    catch (e) { console.error(e); toast.error('Failed to load workers') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])

  if (loading) return <Spinner label="Loading workers…" />

  return (
    <div>
      <PageHeader title="Workers / Crew" subtitle="Field crew & pay rates for job costing"
        actions={<Button onClick={() => setEditing('new')}><Plus size={16} /> New Worker</Button>} />

      <Card className="p-5">
        <DataTable
          rows={workers}
          emptyMessage="No workers yet"
          onRowClick={w => setEditing(w)}
          columns={[
            { key: 'name', header: 'Name', render: w => <span className="font-medium text-gray-900">{[w.last_name, w.first_name].filter(Boolean).join(', ') || '—'}</span> },
            { key: 'pay_rate', header: 'Pay Rate', render: w => currency(w.pay_rate) },
            { key: 'is_foreman', header: 'Foreman', render: w => w.is_foreman ? <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs">Foreman</span> : '—' },
            { key: 'is_active', header: 'Status', render: w => w.is_active
              ? <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs">Active</span>
              : <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-500 text-xs">Inactive</span> },
          ]}
        />
      </Card>

      {editing && <WorkerForm worker={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load() }} />}
    </div>
  )
}

function WorkerForm({ worker, onClose, onSaved }: { worker: Worker | null; onClose: () => void; onSaved: () => void }) {
  const [first, setFirst] = useState(worker?.first_name ?? '')
  const [last, setLast] = useState(worker?.last_name ?? '')
  const [rate, setRate] = useState(worker?.pay_rate != null ? String(worker.pay_rate) : '')
  const [foreman, setForeman] = useState(!!worker?.is_foreman)
  const [active, setActive] = useState(worker?.is_active ?? true)
  const [busy, setBusy] = useState(false)

  const save = async () => {
    if (!last.trim() && !first.trim()) { toast.error('Enter a name'); return }
    setBusy(true)
    try {
      await saveWorker({
        id: worker?.id,
        first_name: first.trim() || null, last_name: last.trim() || null,
        pay_rate: parseFloat(rate) || 0, is_foreman: foreman, is_active: active,
      })
      toast.success('Worker saved'); onSaved()
    } catch (e) { console.error(e); toast.error('Failed to save worker') }
    finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">{worker ? 'Edit Worker' : 'New Worker'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm"><span className="block text-gray-600 mb-1">First Name</span>
              <input value={first} onChange={e => setFirst(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></label>
            <label className="text-sm"><span className="block text-gray-600 mb-1">Last Name</span>
              <input value={last} onChange={e => setLast(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></label>
          </div>
          <label className="text-sm block"><span className="block text-gray-600 mb-1">Pay Rate ($/hr)</span>
            <input type="number" step="0.01" value={rate} onChange={e => setRate(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" /></label>
          <div className="flex items-center gap-6">
            <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={foreman} onChange={e => setForeman(e.target.checked)} className="h-4 w-4" /> Foreman</label>
            <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} className="h-4 w-4" /> Active</label>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy}><Save size={15} /> Save</Button>
        </div>
      </Card>
    </div>
  )
}
